/**
 * Changing a booking's package or hours from the console (Sep 2026).
 *
 * Lavona Hampton's checkout reset to the form defaults and she signed for 4h Photo + Video when she
 * had chosen 4-Hour Photography; the correction needed hand-written SQL. This module is the rule
 * behind a button instead: the new package price comes from the same tables the checkouts use,
 * everything else on the booking (travel fee, paid add-ons, plan processing fee, late-booking fee)
 * is kept, and the total is recomputed. The server writes it; the view recomputes the balance.
 *
 * Contract: a change confirmed in writing is a binding modification under section 1.4, so no
 * re-signing is asked for. The audit row and the client's confirmation email are the record.
 */
import { getPhotoPackagePrice, getVideoOnlyPackagePrice, getPhotoVideoPackagePrice } from '../constants/packagePricing.js'
import { getBookingProcessingFeeCents } from './financials.js'

export const PACKAGE_TYPES = [
  { key: 'photo', label: 'Photography', hasPhoto: true, hasVideo: false },
  { key: 'photoVideo', label: 'Photo + Video', hasPhoto: true, hasVideo: true },
  { key: 'videoOnly', label: 'Videography', hasPhoto: false, hasVideo: true }
]
export const HOURS = Array.from({ length: 12 }, (_, i) => i + 1)
export const MIN_REASON_CHARS = 8

const TYPE_ALIASES = { photo: 'photo', photoOnly: 'photo', photography: 'photo', photoVideo: 'photoVideo', photoAndVideo: 'photoVideo', photo_video: 'photoVideo', videoOnly: 'videoOnly', video: 'videoOnly' }

/** The booking's package type in the three-value vocabulary the price tables use. */
export function normalizePackageType(value) {
  return TYPE_ALIASES[String(value || '').trim()] || null
}

export function packagePriceDollars(type, hours) {
  const h = Number(hours)
  if (type === 'photo') return Number(getPhotoPackagePrice(h)) || 0
  if (type === 'photoVideo') return Number(getPhotoVideoPackagePrice(h)) || 0
  if (type === 'videoOnly') return Number(getVideoOnlyPackagePrice(h)) || 0
  return 0
}

export function packageTitle(type, hours) {
  const t = PACKAGE_TYPES.find(p => p.key === type)
  return `${hours} hour${Number(hours) === 1 ? '' : 's'} - ${t ? t.label : 'Package'}`
}

/** What the booking carries besides the package, in cents, read from where each checkout stored it. */
export function bookingExtrasCents(booking) {
  const pd = booking?.personalization_data || {}
  const ps = pd.pricing_summary || {}
  const cents = v => Math.round(Number(v || 0) * 100)
  const travel = cents(booking?.travel_fee) || cents(pd.travelFee) || Number(ps.travel_fee_cents || 0)
  const lateBooking = Number(ps.rush_fee_cents || 0) || cents(booking?.late_booking_fee) || Number(ps.late_fee_cents || 0)
  const paidAddons = (Array.isArray(pd.addons) ? pd.addons : []).filter(a => a && !a.isFree).reduce((s, a) => s + cents(a.price) * (Number(a.qty) || 1), 0)
  const processing = getBookingProcessingFeeCents(booking)
  // Kept from a cancelled booking when it was converted to this package (29 Sep 2026); carried through later changes
  const kept = Number(ps.kept_from_cancelled_cents || 0)
  return { travel, lateBooking, paidAddons, processing, kept }
}

/**
 * Validate a requested change against the booking as it stands.
 * @returns {{ ok: true, type: string, hours: number, reason: string } | { ok: false, message: string }}
 */
export function validatePackageChange({ booking, newType, newHours, reason, convertFromCancelled = false }) {
  const type = normalizePackageType(newType)
  const hours = Number(newHours)
  if (!type) return { ok: false, message: 'Choose Photography, Photo + Video, or Videography.' }
  if (!Number.isInteger(hours) || hours < 1 || hours > 12) return { ok: false, message: 'Hours must be a whole number from 1 to 12.' }
  const currentType = normalizePackageType(booking?.package_type || booking?.personalization_data?.package?.type)
  const currentHours = Number(booking?.hours_booked || booking?.personalization_data?.package?.hoursBooked || 0)
  // Converting keeps the package when it was already changed by hand (Dana Meyers, 29 Sep 2026): only the kept line is added
  if (type === currentType && hours === currentHours && !convertFromCancelled) return { ok: false, message: 'That is already the package on this booking.' }
  if (!packagePriceDollars(type, hours)) return { ok: false, message: 'No price on the table for that package and hours.' }
  const target = PACKAGE_TYPES.find(p => p.key === type)
  if (!target.hasVideo && booking?.videographer_id) return { ok: false, message: 'A videographer is assigned. Unassign them on the booking first, then change the package.' }
  if (!target.hasVideo && booking?.second_videographer_id) return { ok: false, message: 'A second videographer is assigned. Unassign them first, then change the package.' }
  if (!target.hasPhoto && (booking?.photographer_id || booking?.second_photographer_id) && currentType !== 'videoOnly') {
    // Photographer slots stay filled by the placeholder on many bookings; only block a real second photographer.
    if (booking?.second_photographer_id) return { ok: false, message: 'A second photographer is assigned. Unassign them first, then change the package.' }
  }
  const trimmed = String(reason || '').trim()
  if (trimmed.length < MIN_REASON_CHARS) return { ok: false, message: `A reason of at least ${MIN_REASON_CHARS} characters is required. It goes on the audit log.` }
  return { ok: true, type, hours, reason: trimmed }
}

/**
 * The booking after the change: new package price, everything else kept, total recomputed.
 * @returns {{ type, hours, title, photos, packageCents, extras, totalCents, before: { type, hours, title, packageCents, totalCents } }}
 */
export function computePackageChange({ booking, newType, newHours, convertFromCancelled = false, paidCents = 0 }) {
  const type = normalizePackageType(newType)
  const hours = Number(newHours)
  const extras = bookingExtrasCents(booking)
  const packageCents = Math.round(packagePriceDollars(type, hours) * 100)
  let totalCents = packageCents + extras.travel + extras.lateBooking + extras.paidAddons + extras.processing + extras.kept
  // Converted from a cancelled booking (Traci and Kendra, 29 Sep 2026: a paid-in-full wedding that becomes a 2-hour
  // session): what was already paid beyond the new total is kept as its own line, so nothing reads as an overpayment
  // and nothing is owed back.
  const keptAdded = convertFromCancelled ? Math.max(0, Number(paidCents || 0) - totalCents) : 0
  totalCents += keptAdded
  const target = PACKAGE_TYPES.find(p => p.key === type)
  const pkg = booking?.personalization_data?.package || {}
  const beforeType = normalizePackageType(booking?.package_type || pkg.type)
  const beforeHours = Number(booking?.hours_booked || pkg.hoursBooked || 0)
  return {
    type, hours,
    title: packageTitle(type, hours),
    photos: target?.hasPhoto ? 50 * hours : 0,
    packageCents, extras, totalCents, keptAdded, keptCents: extras.kept + keptAdded,
    before: {
      type: beforeType, hours: beforeHours,
      title: pkg.packageTitle || packageTitle(beforeType, beforeHours),
      packageCents: Math.round(Number(pkg.packagePrice || 0) * 100) || Number(booking?.personalization_data?.pricing_summary?.package_price_cents || 0),
      totalCents: Math.max(Number(booking?.package_total_cents || 0), Math.round(Number(booking?.total_amount || 0) * 100))
    }
  }
}

/** Steps shown before the confirm click. */
export function describePackageChangePlan(change, { paidCents = 0, draftStatus = null } = {}) {
  const d = c => '$' + (Number(c) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  const kept = []
  if (change.extras.travel) kept.push(`${d(change.extras.travel)} travel`)
  if (change.extras.paidAddons) kept.push(`${d(change.extras.paidAddons)} add-ons`)
  if (change.extras.processing) kept.push(`${d(change.extras.processing)} plan processing fee`)
  if (change.extras.lateBooking) kept.push(`${d(change.extras.lateBooking)} late booking fee`)
  if (change.extras.kept) kept.push(`${d(change.extras.kept)} kept from a cancelled booking`)
  const steps = [
    `Change the package from ${change.before.title} to ${change.title} (${d(change.before.packageCents)} → ${d(change.packageCents)} from the price table)`,
    kept.length ? `Keep ${kept.join(', ')} as they are` : 'No travel, add-ons, or plan fee on this booking to keep',
    ...(change.keptAdded ? [`Keep ${d(change.keptAdded)} of what was paid as "Kept from the cancelled ${change.type === change.before.type && change.hours === change.before.hours ? 'booking' : change.before.title}": the ledger stays level, nothing is owed back, and it does not show as an overpayment`] : []),
    `Total goes from ${d(change.before.totalCents)} to ${d(change.totalCents)}; ${d(paidCents)} already paid counts toward it`,
    'Update the client\'s portal and the Booking Record; the contract stays signed (a written change is a binding modification under section 1.4)'
  ]
  if (draftStatus === 'draft') steps.push('The draft final invoice in Stripe is adjusted to the new balance when it is finalized')
  if (draftStatus === 'open') steps.push('An open Stripe invoice still shows the old amount: void it and create a new one after this')
  steps.push('Write who did this, when, and why to the audit log')
  return steps
}
